import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, OnInit, ViewChild, inject } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { ActivatedRoute } from '@angular/router'
import { interval, take } from 'rxjs'
import { ConfirmationService, MessageService } from 'primeng/api'
import { Popover } from 'primeng/popover'
import { TranslateService } from '@ngx-translate/core'
import { allPermissions } from '@app/core/auth/permissions'
import { AuthService } from '@app/core/auth/services/auth.service'
import { NavbarService } from '@app/core/layout/services/navbar.service'
import { ClientRoleService } from '@app/modules/nursing/settings/services/client-role.service'
import { UsersService } from '@app/shared/pages/settings/services/users.service'
import { BookableArea, Booking, BookingStatus, BookingUsageGroupBy, BookingUsageRow } from '@app/models/booking.model'
import { BookingsService } from './services/bookings.service'
import { BookingAllowanceConfirmService } from './services/booking-allowance-confirm.service'
import { BookingTimeService } from './services/booking-time.service'
import { BookingFormComponent } from './booking-form/booking-form.component'

/**
 * A booking plus its times on the CLIENT'S wall clock, resolved once when the rows land.
 *
 * Precomputed rather than converted in the template: the grid, the calendar layout and the day
 * comparisons all need the same two values, and a conversion method called from a template would
 * re-run for every cell on every change-detection pass.
 */
export interface BookingRow extends Booking {
  start_wall: Date
  end_wall: Date
  /**
   * When the room was actually given back, on the same clock — null unless somebody ended the
   * meeting by hand.
   *
   * `end_booking` stamps `ended_at` and deliberately leaves `end_time` where it was, so these two
   * disagree exactly when a meeting broke up early. Carried alongside the other two because every
   * honest statement this page makes about a room being OCCUPIED is about this end, not that one.
   */
  ended_wall: Date | null
}

/**
 * What one calendar row is a row OF.
 *
 * `day` puts the rooms on the Y axis, which is the useful axis for a client with a corridor of
 * them. `week` puts the days there instead — for a client with one or two rooms the room axis is
 * nearly constant, so it spends the whole screen on the one thing that does not vary.
 */
export type CalendarMode = 'day' | 'week'

/**
 * What one booking DID to the room, as against what it booked.
 *
 * These two ends part company for one reason. `end_booking` stamps `ended_at` and leaves
 * `end_time` alone, and PAPI's conflict check only defends `booked` / `in_use` rows — so the
 * moment a meeting breaks up early the room is genuinely free and somebody else may legally take
 * the rest of the hour. That is the ONLY way two drawn bookings can ever overlap: two live rows
 * never can. Holding the ends apart here is what lets the row draw the room's real occupancy
 * instead of a window nobody was sitting in.
 */
interface BookingSpan {
  booking: BookingRow
  /** Scheduled bounds, in minutes since the row's midnight. */
  start: number
  end: number
  /** Where the room went back to the pool: `ended_at` when it undercuts `end_time`, else `end`. */
  heldEnd: number
}

/** One booking drawn on the calendar, already laid out as percentages of the visible day. */
interface CalendarBlock {
  booking: BookingRow
  leftPct: number
  widthPct: number
  /**
   * Which stacked band of the row this block sits in, counting from the top.
   *
   * Always 0 on a row where nothing overlaps, which is nearly every row — a lane is what a
   * collision costs, and a row that has none pays nothing for the machinery.
   */
  lane: number
  label: string
  /** Always LTR — a clock range reordered by the bidi algorithm reads as end–start. */
  time: string
  tooltip: string
  /**
   * Faded because a search is running and this booking is not one of its answers.
   *
   * Dimmed and not removed: a calendar that drops blocks is claiming the room is free at that
   * hour, which is the one thing this screen must never say wrongly. The search narrows what the
   * eye lands on; it does not narrow what is true about the rooms.
   */
  dimmed: boolean
}

/** A stretch the room is shut for, drawn as shading so empty space reads as "closed", not "free". */
interface CalendarClosed {
  leftPct: number
  widthPct: number
}

/**
 * The tail a booking GAVE BACK: booked, then handed over when the meeting ended early.
 *
 * Drawn as an outline rather than filled, and behind everything: the room was empty here, so
 * anything solid would repeat the lie the block used to tell on its own. Already clipped at
 * whoever claimed the time next, which is what makes the ordinary case — one person leaves, the
 * next takes the rest of the hour — draw as two blocks in a row instead of two stacked on top of
 * each other. It carries no pointer events, because the free-time hit area underneath it is now
 * the true thing about this stretch.
 */
interface CalendarReleased {
  leftPct: number
  widthPct: number
}

/**
 * A free, bookable stretch — inside the room's opening hours, not already taken, not in the past.
 * Clicking one starts a booking at the clicked position, so the empty half of the calendar becomes
 * the way bookings are made rather than dead space.
 */
interface CalendarGap {
  leftPct: number
  widthPct: number
  /** Wall-clock bounds; the click position picks a slot inside them. */
  start: Date
  end: Date
}

/**
 * The slot a hover is proposing, drawn inside the free stretch it was measured in.
 *
 * A free stretch is one element spanning hours, so pointing at it highlighted every free hour of
 * the day at once — an invitation to click that never said what clicking would book. This follows
 * the pointer instead and marks exactly the slot that would be taken.
 */
interface CalendarHover {
  rowKey: string
  /** Minutes since the row's midnight — the identity of the slot, so an unchanged one is not redrawn. */
  startMinutes: number
  leftPct: number
  widthPct: number
  label: string
}

/**
 * One line of the calendar — always ONE room on ONE day, whichever axis the mode puts on top.
 *
 * The leading column names whichever of the two the mode is not already grouping by, so every row
 * of a screen is labelled the same way as every other: rooms under a day heading in the week view,
 * rooms straight down in the day view. Mixing the two in one column — a date on the first row and
 * bare room names under it — made two rooms of the same client look like two kinds of thing.
 */
interface CalendarRow {
  key: string
  area: BookableArea
  /** Midnight of the day this row covers, on the client's clock. */
  date: Date
  /** The day heading drawn as a band ABOVE this row. Set on the first row of each grouped day. */
  groupLabel: string
  /** What the leading column says: the room, or the day when the room axis holds one room. */
  rowLabel: string
  branchLabel: string
  title: string
  /** Draws the now-marker and tints the row. */
  isToday: boolean
  blocks: CalendarBlock[]
  released: CalendarReleased[]
  closed: CalendarClosed[]
  gaps: CalendarGap[]
  /**
   * How many bands the blocks are split into — 1 unless something in this row overlaps.
   *
   * The row's height follows it, because the blocks are absolutely positioned and a track full of
   * them will not grow by itself. Above 1 the block drops to a single line: two lanes in 44px is
   * 17px each, which fits a clock or a name but not both stacked.
   */
  lanes: number
}

/**
 * Bookings — the admin-facing side of room booking (YB-2073).
 *
 * Three tabs over one date range: a grid of bookings with the portal actions, a room-by-time
 * calendar built from the SAME rows (no second query), and the credits usage report.
 *
 * Everything the portal is allowed to do is a portal-governed action in PAPI, and all of it is
 * per-action permissioned: cancelling a colleague's meeting and evicting someone out of a room
 * they are sitting in are different levels of trust.
 */
@Component({
  selector: 'app-bookings',
  templateUrl: './bookings.component.html',
  styleUrls: ['./bookings.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BookingsComponent implements OnInit {
  /** How much the quick-extend adds. Also priced in its confirmation, so it lives in one place. */
  private static readonly EXTEND_MINUTES = 30

  /** The window to draw when no room in view declares opening hours — nothing better is known. */
  private static readonly DEFAULT_FROM_HOUR = 6
  private static readonly DEFAULT_TO_HOUR = 22

  /** Never narrower than this, or a room open for one hour a day collapses to a single column. */
  private static readonly MIN_WINDOW_HOURS = 4

  /**
   * Up to this many rooms the week view is the better default: it is the room axis that is nearly
   * empty for these clients. Past it, seven days × rooms stops fitting on a screen.
   */
  private static readonly WEEK_DEFAULT_MAX_ROOMS = 3

  /** What a click on empty space proposes, before the room's own slot and duration rules trim it. */
  private static readonly NEW_BOOKING_MINUTES = 60

  /**
   * How often the page re-reads itself.
   *
   * This is a shared screen over shared rooms: somebody books from the app while an admin is
   * looking at that very hour, and a room in use becomes a room free with nobody touching the
   * browser. A minute is under the shortest bookable slot, so the picture is never a slot behind —
   * and re-querying a whole week faster than that would mostly redraw the same pixels.
   */
  private static readonly REFRESH_MS = 60_000

  private destroyRef = inject(DestroyRef)

  @ViewChild('bookingForm') bookingForm: BookingFormComponent

  /** The details panel that opens on a calendar block. */
  @ViewChild('blockPanel') blockPanel: Popover

  permissions = allPermissions

  loading = false

  /**
   * Tab order, named because the filter bar, the usage loader and the auto-refresh all key off it.
   *
   * The calendar is first on purpose: this page is opened to see what is happening in the rooms,
   * and that is a picture, not a list. The list answers a narrower question — "find me this one
   * booking" — and it is also the one place the word "Bookings" was being said twice, once as the
   * page title and once as the tab under it.
   */
  readonly tabs = { calendar: 0, grid: 1, usage: 2 }
  activeTab = this.tabs.calendar

  /** Every bookable room in the client — also the source of the room filter. */
  areas: BookableArea[] = []

  /**
   * Whether any group in this client actually books against a credit pool — see `usesCredits`.
   *
   * Starts false so a client that meters nothing never flashes a credits column on the way in; the
   * roles land once per session (the service caches them) and turn it on for the ones that do.
   */
  private anyGroupHasAllowance = false
  bookings: BookingRow[] = []
  filteredBookings: BookingRow[] = []

  /** Filters. `dateRange` is a two-date picker; a single date means "that day". */
  dateRange: Date[] = []
  selectedEventId: number | null = null
  selectedAreaIds: number[] = []
  selectedStatus: BookingStatus | null = null
  search = ''

  /** What the status filter offers on the CURRENT tab — see `applyStatusOptions`. */
  statusOptions: { label: string; value: BookingStatus }[] = []
  private allStatusOptions: { label: string; value: BookingStatus }[] = []

  /** Calendar tab — one row per room per visible day, on the client's clock. */
  calendarDate: Date = new Date()
  calendarRows: CalendarRow[] = []

  /**
   * The booking whose details panel is open, and whether that panel is currently showing.
   *
   * Held apart from the panel's own state because both are read for different things: the booking
   * is what the panel draws, and the flag is what tells the auto-refresh to stand down. Kept set
   * while the panel animates out, so it does not blank halfway through closing.
   */
  selectedBooking: BookingRow | null = null
  bookingPanelOpen = false

  /**
   * The visible hour window. Derived from the rooms' opening hours rather than fixed, because a
   * hard 06:00–22:00 spent a third of the width on hours nobody in this client can book — the
   * single biggest source of the emptiness, and one that has nothing to do with how many rooms
   * there are. Falls back to the constants above when no room declares any hours.
   */
  calendarFromHour = BookingsComponent.DEFAULT_FROM_HOUR
  calendarToHour = BookingsComponent.DEFAULT_TO_HOUR

  /**
   * Starts on the day view and switches itself to the week view once the rooms are known and there
   * are few enough of them. `calendarModeChosen` freezes that the moment the admin picks a side:
   * a layout that keeps re-deciding itself under someone who has already chosen is disorienting,
   * and adding a fourth room should not silently take away the view they had got used to.
   */
  calendarMode: CalendarMode = 'day'
  private calendarModeChosen = false

  /** The slot the pointer is currently proposing over a free stretch, or null. */
  gapHover: CalendarHover | null = null

  /**
   * One column per hour INTERVAL (06:00–07:00 … 21:00–22:00), not per hour boundary. The label
   * sits on the column's leading edge, so a label and the gridline under it mark the same instant —
   * with one column per boundary they were half a column apart and every block looked misplaced.
   */
  readonly calendarLegend = [
    { status: 'booked', labelKey: 'BOOKINGS.STATUS.BOOKED' },
    { status: 'in_use', labelKey: 'BOOKINGS.STATUS.IN_USE' },
    { status: 'completed', labelKey: 'BOOKINGS.STATUS.COMPLETED' },
    { status: 'no_show', labelKey: 'BOOKINGS.STATUS.NO_SHOW' },
  ]

  /** Usage report tab. */
  usageGroupBy: BookingUsageGroupBy = 'client_role'
  usageRows: BookingUsageRow[] = []
  usageLoading = false
  groupByOptions: { label: string; value: BookingUsageGroupBy }[] = []

  constructor(
    private bookingsService: BookingsService,
    private allowanceConfirm: BookingAllowanceConfirmService,
    public bookingTime: BookingTimeService,
    private navbarService: NavbarService,
    private usersService: UsersService,
    private clientRoleService: ClientRoleService,
    private authService: AuthService,
    private route: ActivatedRoute,
    private confirmationService: ConfirmationService,
    private messageService: MessageService,
    public translate: TranslateService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.assignTitle()
    this.buildOptions()
    this.restoreCalendarMode()
    this.loadCreditsInUse()
    this.startAutoRefresh()

    // The client's offset gates everything else: the default date range, "today" on the calendar
    // and the boundaries sent to PAPI are all expressed on the client's clock, so loading rows
    // first would date-stamp them against the browser's and then silently re-interpret them.
    this.bookingTime.reset()
    this.bookingTime
      .load()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.calendarDate = this.bookingTime.nowWall()
        this.resetDateRangeToThisWeek()
        this.applyDeepLink()
        this.loadAreas()
        this.load()
      })

    this.translate.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.assignTitle()
      this.buildOptions()
      this.cdr.markForCheck()
    })
  }

  private assignTitle(): void {
    this.navbarService.title.next(this.translate.instant('BOOKINGS.TITLE'))
  }

  /**
   * Arriving from somewhere that already knows which room is being asked about (YB-2066).
   *
   * The authorized dashboard draws a room's day as a card and sends the click here; landing on the
   * whole client's calendar would make the reader find that one room again by hand. Both params
   * are optional and both are just the page's own filters pre-set, so everything downstream — the
   * loads below, the branch and room selects — behaves as though an admin had chosen them.
   *
   * Read once from the snapshot rather than subscribed: this is an entry condition, and reacting to
   * later query-param changes would fight the filters the admin then adjusts on the page.
   */
  private applyDeepLink(): void {
    const params = this.route.snapshot.queryParamMap
    const eventId = Number(params.get('event_id'))
    const areaId = Number(params.get('area_id'))

    if (Number.isFinite(eventId) && eventId > 0) this.selectedEventId = eventId
    if (Number.isFinite(areaId) && areaId > 0) this.selectedAreaIds = [areaId]
  }

  /* ─────────────────────────── keeping it current ───────────────────────── */

  private startAutoRefresh(): void {
    interval(BookingsComponent.REFRESH_MS)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.autoRefresh())
  }

  /**
   * A background re-read, deliberately not a plain `load()`.
   *
   * It carries no spinner — a table that blinks every minute is worse than one that is a minute
   * stale — and it stands down whenever refreshing would pull the ground from under someone: a
   * hidden tab has no reader to serve, the drawer is holding a half-typed booking whose room list
   * must not change underneath it, a pointer resting on a free slot is an admin taking aim, and an
   * open details panel is an admin reading the booking they are about to cancel — a redraw would
   * destroy the block it is anchored to and leave it pointing at nothing.
   */
  private autoRefresh(): void {
    if (document.hidden || this.loading || this.gapHover || this.bookingPanelOpen || this.bookingForm?.visible) return
    this.load(true)
    if (this.activeTab === this.tabs.usage) this.loadUsage()
    // Worth a redraw even when nothing came back changed: the now-marker has moved.
    this.cdr.markForCheck()
  }

  /**
   * The day/week choice, restored from the admin's own preferences on the server.
   *
   * Stored beside their KPIs and their language rather than in this browser, so it follows them to
   * whatever machine they sign in on and cannot be handed to a colleague by a shared computer.
   *
   * It arrives asynchronously, and may land after the rooms have picked a default — so a stored
   * choice always wins and rebuilds. Before the client's clock has landed there is nothing drawn
   * and nothing requested yet; the first load will use the restored mode by itself.
   */
  private restoreCalendarMode(): void {
    this.usersService
      .getCurrentUserPrefs()
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (prefs) => {
          const stored = prefs?.bookingsCalendarMode
          const mode: CalendarMode | null = stored === 'day' || stored === 'week' ? stored : null
          if (!mode) return
          this.calendarModeChosen = true
          if (this.calendarMode !== mode) {
            this.calendarMode = mode
            // Through setCalendarDate once the range exists: a week may reach outside what is loaded.
            if (this.dateRange.length) this.setCalendarDate(this.calendarDate)
          }
          this.cdr.markForCheck()
        },
        // No prefs, or no session yet. The room-count default is a fine answer.
        error: () => undefined,
      })
  }

  /**
   * Asks the groups whether anyone in this client is actually spending against a pool.
   *
   * `getClientRoles` is cached for the session by the service and read by several other screens,
   * so in practice this is a cache hit rather than a request of our own. A failure leaves it off:
   * the credits are then simply not mentioned, which is the same screen a non-metering client
   * gets — where the other way round would invent a quota column out of an error.
   */
  private loadCreditsInUse(): void {
    this.clientRoleService
      .getClientRoles(null)
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (roles) => {
          this.anyGroupHasAllowance = (roles ?? []).some((role) => Number(role?.booking?.allowance?.credits) > 0)
          this.cdr.markForCheck()
        },
        error: () => undefined,
      })
  }

  /** Written silently — a toast on every tap of a view toggle is noise, not confirmation. */
  private rememberCalendarMode(): void {
    this.usersService
      .saveCurrentUserPrefs({ bookingsCalendarMode: this.calendarMode }, true)
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe({ error: () => undefined })
  }

  private buildOptions(): void {
    const statuses: BookingStatus[] = ['booked', 'in_use', 'completed', 'canceled', 'no_show']
    this.allStatusOptions = statuses.map((value) => ({ value, label: this.translate.instant(`BOOKINGS.STATUS.${value.toUpperCase()}`) }))
    this.applyStatusOptions()
    const groups: BookingUsageGroupBy[] = ['client_role', 'pass', 'area']
    this.groupByOptions = groups.map((value) => ({ value, label: this.translate.instant(`BOOKINGS.GROUP_BY.${value.toUpperCase()}`) }))
  }

  /**
   * Drops "Cancelled" from the status filter while the calendar is showing.
   *
   * A cancelled booking has no time on the calendar — its slot went back to being free, and
   * drawing it would claim a room is taken when it is not, so `buildRow` skips them. Filtering by
   * it therefore produced a screen of empty rows that never said why. The option is removed rather
   * than the emptiness explained: an option that can only ever return nothing is the same silent
   * lie as a filter that changes nothing, and this page already refuses to show those.
   *
   * Assigned as a whole array so the reference is stable between change-detection passes; a getter
   * returning a fresh `filter()` would hand the select a new list on every tick.
   */
  private applyStatusOptions(): void {
    this.statusOptions = this.activeTab === this.tabs.calendar ? this.allStatusOptions.filter((option) => option.value !== 'canceled') : this.allStatusOptions
  }

  /**
   * The current calendar week, Sunday to Saturday — not "the next seven days".
   *
   * The two differ by whatever part of the week has already happened, and the calendar's week view
   * draws a real week. Loading a rolling window instead would leave the first rows of the week
   * blank on any day but Sunday, and cost a second round trip to fill them in.
   */
  private resetDateRangeToThisWeek(): void {
    const from = this.weekStart(this.bookingTime.nowWall())
    const to = new Date(from)
    to.setDate(to.getDate() + 6)
    this.dateRange = [from, to]
  }

  /** Sunday. The opening-hours dialect numbers its days from Sunday too, so the two agree. */
  private weekStart(date: Date): Date {
    const start = new Date(date)
    start.setHours(0, 0, 0, 0)
    start.setDate(start.getDate() - start.getDay())
    return start
  }

  /**
   * The window to ask PAPI for. The picker deals in the client's wall clock, so midnight-to-
   * midnight here is the client's day — converted to the absolute instants the wire expects.
   *
   * (The picker hands back [from, null] mid-selection; treat that as a single day.)
   */
  private get range(): { from: Date; to: Date } {
    const from = this.dateRange?.[0] ? new Date(this.dateRange[0]) : this.bookingTime.nowWall()
    from.setHours(0, 0, 0, 0)
    const to = this.dateRange?.[1] ? new Date(this.dateRange[1]) : new Date(from)
    to.setHours(23, 59, 59, 999)
    return { from: this.bookingTime.toUtc(from), to: this.bookingTime.toUtc(to) }
  }

  private loadAreas(): void {
    this.bookingsService
      .getBookableAreas()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        this.areas = result.success && Array.isArray(result.data) ? result.data : []
        // How many rooms there are is the whole question the default mode answers, and it is not
        // known until this lands.
        if (!this.calendarModeChosen && this.areas.length) {
          this.calendarMode = this.branchAreas.length <= BookingsComponent.WEEK_DEFAULT_MAX_ROOMS ? 'week' : 'day'
        }
        // The rooms and the bookings are two independent requests. The calendar draws one row per
        // ROOM, so whichever lands second has to rebuild it — otherwise the common case of the
        // rooms arriving last leaves the tab permanently on "no bookable rooms".
        this.buildCalendar()
        this.cdr.markForCheck()
      })
  }

  /* ─────────────────────────────── branches ─────────────────────────────── */

  /**
   * The page is client-wide, and a client is a set of branches — every one of which is free to
   * name a room "Room B". The branch is therefore part of a room's identity here, not decoration:
   * without it the room filter offers six indistinguishable entries and the grid cannot be read.
   *
   * Derived from the rooms rather than fetched: the rooms already carry their branch, so a client
   * with one branch pays nothing for this and never sees the filter at all.
   */
  get branchOptions(): { label: string; value: number }[] {
    const byId = new Map<number, string>()
    for (const area of this.areas) {
      if (!byId.has(area.event_id)) byId.set(area.event_id, area.event_name || this.translate.instant('BOOKINGS.FILTERS.UNKNOWN_BRANCH'))
    }
    return [...byId.entries()].map(([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label))
  }

  /** One branch is the common case; showing a filter with a single option is just noise. */
  get hasMultipleBranches(): boolean {
    return this.branchOptions.length > 1
  }

  /** The rooms in play: everything, or just the chosen branch's. Drives the room filter AND the calendar. */
  get branchAreas(): BookableArea[] {
    return this.selectedEventId == null ? this.areas : this.areas.filter((area) => area.event_id === this.selectedEventId)
  }

  /**
   * Picking a branch narrows the room list, so any room selected from a DIFFERENT branch is no
   * longer offered — leaving it in the filter would silently constrain the results to rooms the
   * user can no longer see listed.
   */
  /**
   * The multi-select's clear button writes `null` over the model rather than emptying the array,
   * and every reader of it — the calendar included — is entitled to assume it holds a list.
   * Normalised here, at the one place the control can write, rather than defended against in each.
   */
  onRoomsChange(value: number[] | null): void {
    this.selectedAreaIds = value ?? []
  }

  onBranchChange(): void {
    const visible = new Set(this.branchAreas.map((area) => area.area_id))
    this.selectedAreaIds = (this.selectedAreaIds ?? []).filter((id) => visible.has(id))
    this.usageRows = []
    this.load()
    if (this.activeTab === this.tabs.usage) this.loadUsage()
  }

  /** `silent` is the auto-refresh: same query, no spinner, so the page does not blink every minute. */
  load(silent = false): void {
    this.loading = !silent
    this.cdr.markForCheck()
    const { from, to } = this.range
    this.bookingsService
      .getBookings(from, to, { event_id: this.selectedEventId, area_ids: this.selectedAreaIds, status: this.selectedStatus })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        const rows = result.success && Array.isArray(result.data) ? result.data : []
        // Absolute UTC off the wire, the client's wall clock from here on. Done once, here, so no
        // screen downstream has to remember which of the two it is holding.
        this.bookings = rows.map((booking) => ({
          ...booking,
          start_wall: this.bookingTime.toWall(booking.start_time),
          end_wall: this.bookingTime.toWall(booking.end_time),
          ended_wall: booking.ended_at ? this.bookingTime.toWall(booking.ended_at) : null,
        }))
        this.applySearch()
        this.buildCalendar()
        this.loading = false
        this.cdr.markForCheck()
      })
  }

  /**
   * Room / status / date are server-side filters; the free-text box is client-side on the names
   * PAPI already resolved (booker, group, room, title). There is no server-side text search for
   * bookings, and adding one to filter a list that is capped at 2000 rows would buy nothing.
   */
  private matchesSearch(booking: BookingRow): boolean {
    const term = this.search.trim().toLowerCase()
    if (!term) return true
    return [booking.pass_name, booking.client_role_name, booking.area_name, booking.event_name, booking.title].some((field) => (field ?? '').toLowerCase().includes(term))
  }

  applySearch(): void {
    this.filteredBookings = this.bookings.filter((booking) => this.matchesSearch(booking))
    this.cdr.markForCheck()
  }

  /**
   * One box, two behaviours, because the two tabs are different kinds of surface.
   *
   * In the list a search REMOVES rows — the question there is "find me this one booking", and the
   * rows that are not it are in the way. On the calendar it only fades them, because a calendar
   * that removes blocks is saying the room is free at that hour. So the same term that narrows the
   * list to one row leaves the calendar intact and simply picks that booking out of it.
   */
  onSearchChange(): void {
    this.applySearch()
    this.applySearchHighlight()
    this.cdr.markForCheck()
  }

  /**
   * Re-marks the drawn blocks against the current term.
   *
   * Deliberately not a `buildCalendar()`: re-deriving the layout on every keystroke would also
   * throw away the hover and close the details panel, and none of the geometry depends on the
   * search. Only which blocks are answers does.
   */
  private applySearchHighlight(): void {
    for (const row of this.calendarRows) {
      for (const block of row.blocks) block.dimmed = !this.matchesSearch(block.booking)
    }
  }

  onFiltersChange(): void {
    this.load()
  }

  onTabChange(index: unknown): void {
    this.activeTab = Number(index)
    // "Cancelled" is not on offer on the calendar, so carrying it in from the list would leave the
    // screen filtered by a status the reader can no longer see selected — and no way back to it.
    const droppedStatus = this.activeTab === this.tabs.calendar && this.selectedStatus === 'canceled'
    if (droppedStatus) this.selectedStatus = null
    this.applyStatusOptions()
    if (droppedStatus) this.load()
    if (this.activeTab === this.tabs.usage && !this.usageRows.length) this.loadUsage()
  }

  /* ──────────────────────── which filters this tab obeys ─────────────────── */

  /**
   * The filter bar sits above all three tabs, but the three do not read the same filters, and a
   * control that changes nothing on the screen under it is worse than a missing one — it is a
   * silent lie about what is being shown. So each is shown only where it actually bites.
   *
   * The branch narrows every one of the three and is always offered (when there is one to pick).
   */

  /** The calendar owns its own period through the day/week nav; two controls for it would fight. */
  get showDateFilter(): boolean {
    return this.activeTab !== this.tabs.calendar
  }

  /** The usage report is grouped server-side over the whole branch — the room list never reaches it. */
  get showRoomFilter(): boolean {
    return this.activeTab !== this.tabs.usage
  }

  get showStatusFilter(): boolean {
    return this.activeTab !== this.tabs.usage
  }

  /**
   * Free text works on both row-shaped tabs — as a filter in the list, as a highlight on the
   * calendar. The usage report is aggregated server-side into groups, so there are no booking
   * fields left in it for a term to match.
   */
  get showSearchFilter(): boolean {
    return this.activeTab !== this.tabs.usage
  }

  /** The placeholder has to say which of the two the box is about to do. */
  get searchPlaceholderKey(): string {
    return this.activeTab === this.tabs.calendar ? 'BOOKINGS.FILTERS.SEARCH_PLACEHOLDER_CALENDAR' : 'BOOKINGS.FILTERS.SEARCH_PLACEHOLDER'
  }

  /**
   * The grid is one flat row per booking over the whole range: a week of a busy building runs to
   * hundreds, and PAPI's cap is 2000. Paged rather than left as one long scroll, so the count is
   * legible and the DOM stays small — client-side, over rows that are already loaded.
   */
  readonly rowsPerPage = 25
  readonly rowsPerPageOptions = [25, 50, 100]

  get paginationTemplate(): string {
    return this.translate.instant('COMMON.PAGINATION.SHOWING')
  }

  /* ─────────────────────────────── calendar ─────────────────────────────── */

  /** The rooms the calendar draws — the branch's, narrowed by the room filter when one is set. */
  private get visibleAreas(): BookableArea[] {
    const selected = this.selectedAreaIds ?? []
    return selected.length ? this.branchAreas.filter((area) => selected.includes(area.area_id)) : this.branchAreas
  }

  /** Whether the week view has a room axis to group under a day heading, or only the one room. */
  get calendarGrouped(): boolean {
    return this.calendarMode === 'week' && this.visibleAreas.length > 1
  }

  /** The days on screen: one in the day view, the calendar week (Sunday first) in the week view. */
  private visibleDays(): Date[] {
    const base = new Date(this.calendarDate)
    base.setHours(0, 0, 0, 0)
    if (this.calendarMode === 'day') return [base]
    const start = this.weekStart(base)
    return Array.from({ length: 7 }, (_, index) => {
      const day = new Date(start)
      day.setDate(day.getDate() + index)
      return day
    })
  }

  /**
   * Lays the already-loaded bookings out as percentage-width blocks, so the calendar costs nothing
   * beyond the grid's own query in either mode — the page loads a whole week up front.
   *
   * Bookings that start before or end after the visible window are clamped to it rather than
   * dropped: a meeting running past the last column must still be seen.
   */
  private buildCalendar(): void {
    // The rows the hover was measured against — and the block the details panel is anchored to —
    // are about to be replaced. A panel left open would float beside an element that no longer
    // exists, describing a booking the reader can no longer point at.
    this.gapHover = null
    this.closeBookingPanel()
    const days = this.visibleDays()
    const areas = this.visibleAreas
    this.applyCalendarWindow(days, areas)

    const spanMinutes = (this.calendarToHour - this.calendarFromHour) * 60
    if (spanMinutes <= 0 || !areas.length) {
      this.calendarRows = []
      return
    }

    const now = this.bookingTime.nowWall()
    const rows: CalendarRow[] = []
    for (const day of days) {
      for (const [index, area] of areas.entries()) {
        rows.push(this.buildRow(day, area, index === 0, areas.length > 1, spanMinutes, now))
      }
    }
    this.calendarRows = rows
  }

  private buildRow(day: Date, area: BookableArea, firstOfDay: boolean, manyRooms: boolean, spanMinutes: number, now: Date): CalendarRow {
    const week = this.calendarMode === 'week'
    const windowStart = this.calendarFromHour * 60
    const windowEnd = this.calendarToHour * 60
    // Minutes since this row's midnight. Doing the arithmetic in minutes-of-day rather than in
    // instants is what lets a booking that runs over midnight land correctly on both rows.
    const minutesOf = (date: Date): number => (date.getTime() - day.getTime()) / 60000
    const rect = (start: number, end: number) => ({ leftPct: ((start - windowStart) / spanMinutes) * 100, widthPct: ((end - start) / spanMinutes) * 100 })

    const spans: BookingSpan[] = []
    for (const booking of this.bookings) {
      if (booking.area_id !== area.area_id || booking.status === 'canceled') continue
      // Wall clock on both sides: the window is the client's day, so comparing it against absolute
      // instants would slide every block by the offset between the two zones.
      const start = minutesOf(booking.start_wall)
      const end = minutesOf(booking.end_wall)
      if (end <= windowStart || start >= windowEnd) continue
      spans.push({ booking, start, end, heldEnd: this.heldEnd(booking, start, end, minutesOf) })
    }
    // Start ascending, the longer of two that start together first, id last. Which lane a booking
    // lands in follows from this order, so the order has to be a property of the bookings and not
    // of whatever sequence the query happened to answer in.
    spans.sort((a, b) => a.start - b.start || b.end - a.end || a.booking.id - b.booking.id)

    // Busy is what BLOCKS a new booking, and PAPI's conflict check reads the same thing: the room
    // is taken until it was given back, not until the calendar once said it would be. Without this
    // the portal shaded a released hour as occupied and offered no way to click it, while the app
    // booked it happily — the two surfaces disagreeing about the same room.
    const busy = spans.map((span) => ({ start: span.start, end: span.heldEnd })).filter((range) => range.end > range.start)
    const lanes = this.packLanes(spans)

    const blocks: CalendarBlock[] = []
    const released: CalendarReleased[] = []
    for (const span of spans) {
      const { booking } = span
      const solidEnd = Math.max(span.heldEnd, span.start)
      const endedEarly = span.heldEnd < span.end && !!booking.ended_wall
      const from = Math.max(span.start, windowStart)
      const clamped = rect(from, Math.max(Math.min(solidEnd, windowEnd), from))
      const label = booking.title || booking.pass_name || `#${booking.id}`
      // The block says what the room DID. The window it was booked for is still the record, so it
      // keeps the tooltip and gets a line of its own in the details panel — but a block drawn to
      // 18:45 that prints "18:30–19:30" inside itself is arguing with its own edge.
      const time = `${this.clock(booking.start_wall.getTime())}–${this.clock((endedEarly ? booking.ended_wall! : booking.end_wall).getTime())}`
      const bookedUntil = endedEarly ? this.translate.instant('BOOKINGS.CALENDAR.BOOKED_UNTIL', { time: this.clock(booking.end_wall.getTime()) }) : ''
      blocks.push({
        booking,
        leftPct: clamped.leftPct,
        widthPct: Math.max(clamped.widthPct, 1),
        lane: lanes.get(span) ?? 0,
        label,
        time,
        tooltip: [time, bookedUntil, label, booking.pass_name, booking.client_role_name].filter(Boolean).join(' · '),
        dimmed: !this.matchesSearch(booking),
      })

      if (!endedEarly) continue
      // Clipped at whoever claimed the freed time next — including a booking that was already
      // running when this one let go, which swallows the tail whole. This is what turns the shape
      // the whole fix is about (one block sitting on top of another, same colour, two clock ranges
      // fused into one grey slab) into two blocks standing side by side.
      const tailStart = Math.max(solidEnd, windowStart)
      let tailEnd = Math.min(span.end, windowEnd)
      for (const other of spans) {
        if (other === span || other.start >= tailEnd || other.end <= solidEnd) continue
        tailEnd = Math.min(tailEnd, Math.max(other.start, solidEnd))
      }
      if (tailEnd - tailStart >= 1) released.push(rect(tailStart, tailEnd))
    }

    const open = this.openRanges(area, day)
    const closed: CalendarClosed[] = []
    let edge = windowStart
    for (const range of open) {
      if (range.start > edge) closed.push(rect(edge, range.start))
      edge = Math.max(edge, range.end)
    }
    if (edge < windowEnd) closed.push(rect(edge, windowEnd))

    const gaps: CalendarGap[] = []
    const slot = Math.max(area.slot_minutes || 30, 5)
    const nowMinutes = minutesOf(now)
    for (const range of open) {
      let from = range.start
      for (const taken of busy) {
        if (taken.end <= from || taken.start >= range.end) continue
        if (taken.start > from) this.pushGap(gaps, day, from, taken.start, slot, nowMinutes, rect)
        from = Math.max(from, taken.end)
      }
      if (from < range.end) this.pushGap(gaps, day, from, range.end, slot, nowMinutes, rect)
    }

    const dayName = `${this.weekdayName(day)} ${String(day.getDate()).padStart(2, '0')}/${String(day.getMonth() + 1).padStart(2, '0')}`
    // The week view over SEVERAL rooms puts the day in a band of its own above the group, leaving
    // every row below it saying nothing but its room — so the room names all read alike. Over ONE
    // room there is no room axis worth a column, so the day becomes the row label itself and no
    // band is drawn. Either way a column holds one kind of label, never two.
    const grouped = week && manyRooms
    const branchLabel = !week && this.hasMultipleBranches && area.event_name ? area.event_name : ''
    return {
      key: `${day.getTime()}-${area.area_id}`,
      area,
      date: day,
      groupLabel: grouped && firstOfDay ? dayName : '',
      rowLabel: grouped || !week ? area.name : dayName,
      branchLabel,
      title: [week ? dayName : '', area.name, area.event_name].filter(Boolean).join(' · '),
      isToday: day.toDateString() === now.toDateString(),
      blocks,
      released,
      closed,
      gaps,
      lanes: blocks.reduce((most, block) => Math.max(most, block.lane + 1), 1),
    }
  }

  /**
   * Where a booking stopped holding the room, in minutes since the row's midnight.
   *
   * Only a hand-ended booking can undercut its own window: `end_booking` is the one operation that
   * stamps `ended_at`, and it leaves `end_time` alone. A `no_show` keeps its whole window on
   * purpose — nobody walked in, but the reservation stood there all hour keeping everybody else
   * out, and shrinking it to nothing would erase the one fact the amber block exists to report.
   *
   * Clamped into the booking's own window on both sides: `ended_at` is stamped with the clock, so
   * a meeting ended in overtime carries an end LATER than the one it booked, and that must not
   * grow the block past the window it holds elsewhere.
   */
  private heldEnd(booking: BookingRow, start: number, end: number, minutesOf: (date: Date) => number): number {
    if (booking.status !== 'completed' || !booking.ended_wall) return end
    return Math.min(Math.max(minutesOf(booking.ended_wall), start), end)
  }

  /**
   * Which stacked band each booking gets, so an overlap draws as two rows inside the slot.
   *
   * Per overlap CLUSTER rather than per row: one collision at six o'clock must not halve the
   * height of every block in the day. Inside a cluster it is the ordinary interval-graph packing —
   * each span takes the lowest band whose last booking has already finished — which is why the
   * caller's sort has to be total and not merely by start.
   *
   * Only the SOLID part of a span collides. The tail a booking gave back is drawn as an outline
   * behind everything and is the room being FREE, so letting it push a neighbour into a second
   * band would spend height on the absence of a meeting.
   */
  private packLanes(spans: BookingSpan[]): Map<BookingSpan, number> {
    const lanes = new Map<BookingSpan, number>()
    for (let index = 0; index < spans.length; ) {
      const laneEnds: number[] = []
      let clusterEnd = -Infinity
      let member = index
      while (member < spans.length && (member === index || spans[member].start < clusterEnd)) {
        const span = spans[member]
        const solidEnd = Math.max(span.heldEnd, span.start)
        let lane = laneEnds.findIndex((end) => end <= span.start)
        if (lane === -1) lane = laneEnds.push(0) - 1
        laneEnds[lane] = solidEnd
        lanes.set(span, lane)
        clusterEnd = Math.max(clusterEnd, solidEnd)
        member++
      }
      index = member
    }
    return lanes
  }

  /**
   * A free stretch is only offered when something could actually be booked in it: at least one slot
   * long, and not already over. Clicking an hour that has passed would only earn a refusal from
   * PAPI, which validates against the same clock this page draws.
   */
  private pushGap(
    gaps: CalendarGap[],
    day: Date,
    start: number,
    end: number,
    slot: number,
    nowMinutes: number,
    rect: (start: number, end: number) => { leftPct: number; widthPct: number },
  ): void {
    const from = Math.max(start, Math.ceil(nowMinutes / slot) * slot)
    if (end - from < slot) return
    gaps.push({ ...rect(from, end), start: new Date(day.getTime() + from * 60000), end: new Date(day.getTime() + end * 60000) })
  }

  /**
   * When this room takes bookings on this weekday, in minutes since midnight, clipped to the
   * visible window. A room with no hours configured at all is bookable around the clock; a room
   * with hours but none on this weekday is shut for the day and gets no free space drawn.
   */
  private openRanges(area: BookableArea, day: Date): { start: number; end: number }[] {
    const windowStart = this.calendarFromHour * 60
    const windowEnd = this.calendarToHour * 60
    const byDay = area.opening_hours?.days_hours_allowed
    if (!byDay || !Object.keys(byDay).length) return [{ start: windowStart, end: windowEnd }]
    return (byDay[String(day.getDay() + 1)] ?? [])
      .map((range) => this.parseOpeningRange(range))
      .filter((range): range is { start: number; end: number } => range !== null)
      .map((range) => ({ start: Math.max(range.start, windowStart), end: Math.min(range.end, windowEnd) }))
      .filter((range) => range.end > range.start)
      .sort((a, b) => a.start - b.start)
  }

  /** `HHMM-HHMM`, the passes.time_limitations dialect verbatim. Anything else is ignored, not guessed. */
  private parseOpeningRange(value: string): { start: number; end: number } | null {
    const match = /^(\d{2})(\d{2})-(\d{2})(\d{2})$/.exec(String(value).trim())
    if (!match) return null
    const start = Number(match[1]) * 60 + Number(match[2])
    const end = Number(match[3]) * 60 + Number(match[4])
    return end > start ? { start, end } : null
  }

  /**
   * Narrows the drawn hours to the ones the rooms in view are actually open for.
   *
   * A booking outside those hours still widens the window: an early-entry or a hand-made booking
   * that fell outside the rules must not become invisible just because the room says it is shut.
   */
  private applyCalendarWindow(days: Date[], areas: BookableArea[]): void {
    const starts: number[] = []
    const ends: number[] = []

    for (const area of areas) {
      const byDay = area.opening_hours?.days_hours_allowed
      // "Around the clock" is not an opinion about which hours are worth showing.
      if (!byDay || !Object.keys(byDay).length) continue
      for (const day of days) {
        for (const raw of byDay[String(day.getDay() + 1)] ?? []) {
          const range = this.parseOpeningRange(raw)
          if (!range) continue
          starts.push(range.start)
          ends.push(range.end)
        }
      }
    }

    if (!starts.length) {
      starts.push(BookingsComponent.DEFAULT_FROM_HOUR * 60)
      ends.push(BookingsComponent.DEFAULT_TO_HOUR * 60)
    }

    const areaIds = new Set(areas.map((area) => area.area_id))
    const dayKeys = new Set(days.map((day) => day.toDateString()))
    for (const booking of this.bookings) {
      if (!areaIds.has(booking.area_id) || booking.status === 'canceled') continue
      if (!dayKeys.has(booking.start_wall.toDateString())) continue
      starts.push(booking.start_wall.getHours() * 60 + booking.start_wall.getMinutes())
      // A booking ending past midnight belongs to the end of its own day, not the start of the next.
      const sameDay = booking.end_wall.toDateString() === booking.start_wall.toDateString()
      ends.push(sameDay ? booking.end_wall.getHours() * 60 + booking.end_wall.getMinutes() : 24 * 60)
    }

    const from = Math.max(0, Math.floor(Math.min(...starts) / 60))
    let to = Math.min(24, Math.ceil(Math.max(...ends) / 60))
    if (to - from < BookingsComponent.MIN_WINDOW_HOURS) to = Math.min(24, from + BookingsComponent.MIN_WINDOW_HOURS)
    this.calendarFromHour = from
    this.calendarToHour = Math.max(to, from + 1)
  }

  private clock(ms: number): string {
    const date = new Date(ms)
    return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
  }

  /** One entry per hour COLUMN: 06:00 labels the 06:00–07:00 column, so label and gridline agree. */
  get calendarHourColumns(): number[] {
    return Array.from({ length: Math.max(this.calendarToHour - this.calendarFromHour, 0) }, (_, index) => this.calendarFromHour + index)
  }

  /** Whether the period on screen is the current one — the day view's day, the week view's week. */
  get isCalendarToday(): boolean {
    const now = this.bookingTime.nowWall()
    if (this.calendarMode === 'day') return this.calendarDate.toDateString() === now.toDateString()
    return this.weekStart(this.calendarDate).toDateString() === this.weekStart(now).toDateString()
  }

  /**
   * The heading over the calendar. The app registers no Angular locale data, so the `date` pipe
   * would print "Wednesday" even in Hebrew — `Intl` follows the chosen UI language without pulling
   * a locale bundle into the whole app. Only the WEEKDAY comes from the locale: the date itself
   * stays dd/MM/yyyy, which is what every other screen in the portal shows.
   */
  get calendarDateLabel(): string {
    if (this.calendarMode === 'day') return `${this.weekdayName(this.calendarDate)}, ${this.dayMonthYear(this.calendarDate)}`
    const days = this.visibleDays()
    const first = days[0]
    const last = days[days.length - 1]
    return `${String(first.getDate()).padStart(2, '0')}/${String(first.getMonth() + 1).padStart(2, '0')} – ${this.dayMonthYear(last)}`
  }

  private dayMonthYear(date: Date): string {
    return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`
  }

  private weekdayName(date: Date): string {
    const lang = this.translate.currentLang || this.translate.defaultLang || 'en'
    try {
      return new Intl.DateTimeFormat(lang, { weekday: 'long' }).format(date)
    } catch {
      return new Intl.DateTimeFormat('en', { weekday: 'long' }).format(date)
    }
  }

  /**
   * Where "now" falls in the visible hour window, or null when it is off screen. WHICH row draws it
   * is the row's own business — in the week view only today's line carries the marker.
   */
  get calendarNowPct(): number | null {
    if (!this.isCalendarToday) return null
    const now = this.bookingTime.nowWall()
    const minutes = now.getHours() * 60 + now.getMinutes()
    const from = this.calendarFromHour * 60
    const to = this.calendarToHour * 60
    if (to <= from || minutes < from || minutes > to) return null
    return ((minutes - from) / (to - from)) * 100
  }

  setCalendarMode(mode: CalendarMode): void {
    const changed = this.calendarMode !== mode
    // Both recorded even when nothing changed: pressing "Day" while already on it is still a
    // choice. It stops the rooms request from deciding differently when it lands, and it is worth
    // storing for the same reason — otherwise the auto-default reappears on the next machine.
    this.calendarModeChosen = true
    this.calendarMode = mode
    this.rememberCalendarMode()
    if (!changed) return
    // Through setCalendarDate, because the week around the current day may reach outside the
    // loaded range even though the day itself does not.
    this.setCalendarDate(this.calendarDate)
  }

  goToToday(): void {
    if (this.isCalendarToday) return
    this.setCalendarDate(this.bookingTime.nowWall())
  }

  /** One step: a day in the day view, a week in the week view. */
  shiftCalendarDay(steps: number): void {
    const next = new Date(this.calendarDate)
    next.setDate(next.getDate() + steps * (this.calendarMode === 'week' ? 7 : 1))
    this.setCalendarDate(next)
  }

  private setCalendarDate(next: Date): void {
    this.calendarDate = next
    const days = this.visibleDays()
    const wantFrom = days[0]
    const wantTo = new Date(days[days.length - 1])
    wantTo.setHours(23, 59, 59, 999)

    // The period may sit outside the loaded range — widen it rather than showing an empty calendar.
    // Compared against the PICKER's values, which are wall-clock like `next`; `range` has already
    // converted its copies to absolute instants for the wire.
    const from = this.dateRange?.[0] ? new Date(this.dateRange[0]) : new Date(wantFrom)
    from.setHours(0, 0, 0, 0)
    const to = this.dateRange?.[1] ? new Date(this.dateRange[1]) : new Date(from)
    to.setHours(23, 59, 59, 999)
    if (wantFrom < from || wantTo > to) {
      this.dateRange = [new Date(wantFrom), new Date(days[days.length - 1])]
      this.load()
      return
    }
    this.buildCalendar()
    this.cdr.markForCheck()
  }

  /**
   * Start a booking on the empty space that was clicked.
   *
   * The calendar already knows the room, the day and — from where in the gap the click landed — the
   * hour, so re-typing all three into the drawer is work the screen can do itself. The proposal is
   * snapped onto the room's own slot grid and trimmed to its duration limits, so what opens is
   * something PAPI would accept; it is a starting point in a form, not a booking.
   */
  bookGap(row: CalendarRow, gap: CalendarGap, event: MouseEvent): void {
    const slot = this.proposeBooking(row, gap, event)
    if (!slot) return
    this.gapHover = null
    this.bookingForm.open(null, this.areas, { area_id: row.area.area_id, start: slot.start, end: slot.end })
  }

  /**
   * What a click at this position would propose, in minutes since the row's midnight: snapped onto
   * the room's slot grid, trimmed to its duration limits, and kept inside the free stretch's own
   * edges — in that order, so what opens is something PAPI would accept.
   */
  private proposeBooking(row: CalendarRow, gap: CalendarGap, event: MouseEvent): { start: Date; end: Date; startMinutes: number; endMinutes: number } | null {
    const target = event.currentTarget as HTMLElement | null
    if (!target) return null
    const rect = target.getBoundingClientRect()
    if (rect.width <= 0) return null

    // The track's leading edge is on the right in Hebrew, so the fraction is measured from there.
    const offset = document.dir === 'rtl' ? rect.right - event.clientX : event.clientX - rect.left
    const fraction = Math.min(Math.max(offset / rect.width, 0), 1)

    const slot = Math.max(row.area.slot_minutes || 30, 5)
    const gapStart = (gap.start.getTime() - row.date.getTime()) / 60000
    const gapEnd = (gap.end.getTime() - row.date.getTime()) / 60000

    let length = Math.max(Math.round(BookingsComponent.NEW_BOOKING_MINUTES / slot), 1) * slot
    if (row.area.min_duration_minutes) length = Math.max(length, row.area.min_duration_minutes)
    if (row.area.max_duration_minutes) length = Math.min(length, row.area.max_duration_minutes)
    length = Math.min(length, gapEnd - gapStart)
    if (length <= 0) return null

    const pointed = gapStart + fraction * (gapEnd - gapStart)
    let start = Math.floor(pointed / slot) * slot
    start = Math.max(start, Math.ceil(gapStart / slot) * slot)
    if (start + length > gapEnd) start = Math.floor((gapEnd - length) / slot) * slot
    if (start < gapStart) return null

    return {
      start: new Date(row.date.getTime() + start * 60000),
      end: new Date(row.date.getTime() + (start + length) * 60000),
      startMinutes: start,
      endMinutes: start + length,
    }
  }

  /**
   * Marks the slot under the pointer.
   *
   * The free stretch is one element covering every free hour of the day, so lighting IT up said
   * "click somewhere in here" and nothing about what would be booked — which is a strange thing for
   * a control whose whole point is that the position of the click chooses the hour. The highlight
   * now sits on the proposal itself, labelled with its times, and moves as the pointer does.
   */
  onGapHover(row: CalendarRow, gap: CalendarGap, event: MouseEvent): void {
    const slot = this.proposeBooking(row, gap, event)
    if (!slot) {
      this.clearGapHover()
      return
    }
    // Pointer moves fire continuously; only a move onto a DIFFERENT slot changes the screen.
    if (this.gapHover?.rowKey === row.key && this.gapHover.startMinutes === slot.startMinutes) return

    const windowStart = this.calendarFromHour * 60
    const spanMinutes = (this.calendarToHour - this.calendarFromHour) * 60
    this.gapHover = {
      rowKey: row.key,
      startMinutes: slot.startMinutes,
      leftPct: ((slot.startMinutes - windowStart) / spanMinutes) * 100,
      widthPct: ((slot.endMinutes - slot.startMinutes) / spanMinutes) * 100,
      label: `${this.clock(slot.start.getTime())}–${this.clock(slot.end.getTime())}`,
    }
    this.cdr.markForCheck()
  }

  clearGapHover(): void {
    if (!this.gapHover) return
    this.gapHover = null
    this.cdr.markForCheck()
  }

  /* ─────────────────────────────── actions ─────────────────────────────── */

  create(): void {
    this.bookingForm.open(null, this.areas)
  }

  edit(booking: Booking): void {
    this.bookingForm.open(booking, this.areas)
  }

  /* ────────────────────── the calendar's details panel ───────────────────── */

  /**
   * Opens a booking's details beside the block that was clicked.
   *
   * The calendar is the screen this page is opened on and the one that is left open, so it is
   * where a room running over is noticed — and until this panel it was the one screen that could
   * do nothing about it. Clicking a block used to jump straight into the edit drawer, which is a
   * form: it answered "change this booking" while the question in front of the admin was usually
   * "whose is this, and can I have the room back?".
   *
   * `toggle` and not `show`, so a second click on the same block puts it away; PrimeNG re-anchors
   * by itself when the click lands on a different block.
   */
  openBooking(booking: BookingRow, event: MouseEvent): void {
    this.selectedBooking = booking
    // Set before the panel is told to open: it is what the panel draws.
    this.cdr.markForCheck()
    this.blockPanel?.toggle(event)
  }

  onBookingPanelShow(): void {
    this.bookingPanelOpen = true
    this.cdr.markForCheck()
  }

  onBookingPanelHide(): void {
    this.bookingPanelOpen = false
    this.cdr.markForCheck()
  }

  private closeBookingPanel(): void {
    if (!this.bookingPanelOpen) return
    this.blockPanel?.hide()
  }

  /**
   * Whether this booking has any action the current admin may actually take.
   *
   * Both halves matter and neither implies the other: a completed booking offers nothing to
   * anybody, and a read-only viewer may take none of them on anything. Without the check the panel
   * draws an empty, ruled-off action row under half the bookings on the screen — the permission
   * directives having removed every button inside it.
   */
  hasActions(booking: Booking): boolean {
    const components = this.permissions.bookings.groups.general.components
    const may = (permission: string): boolean => !!this.authService.hasPermission(permission)
    // Editing is gated by `create`, the same way the list's pencil is.
    if (this.canEdit(booking) && (may(components.create) || may(components.extend))) return true
    if (this.canCancel(booking) && may(components.cancel)) return true
    if (this.canEvict(booking) && may(components.evict)) return true
    return false
  }

  onFormSaved(): void {
    this.load()
  }

  /**
   * The one sentence explaining why the room was empty for part of its own booking, or ''.
   *
   * The panel's headline is the booked window, because that is the record and it is what the
   * credits were charged against. But that window is exactly what an admin looking at two blocks
   * in the same hour is trying to reconcile, so what the room actually did has to be written down
   * somewhere rather than left to be inferred from the shape of a block.
   */
  occupancyNote(booking: BookingRow): string {
    if (booking.status === 'no_show') return this.translate.instant('BOOKINGS.CALENDAR.NEVER_ENTERED')
    if (booking.status !== 'completed' || !booking.ended_wall) return ''
    if (booking.ended_wall.getTime() >= booking.end_wall.getTime()) return ''
    return this.translate.instant('BOOKINGS.CALENDAR.ENDED_EARLY', { time: this.clock(booking.ended_wall.getTime()) })
  }

  /** `booked` only — cancelling something in use is an eviction, and PAPI refuses it as a cancel. */
  canCancel(booking: Booking): boolean {
    return booking.status === 'booked'
  }

  canEvict(booking: Booking): boolean {
    return booking.status === 'in_use'
  }

  canEdit(booking: Booking): boolean {
    return booking.status === 'booked' || booking.status === 'in_use'
  }

  cancel(booking: Booking): void {
    this.confirmationService.confirm({
      // A portal cancel always refunds in full regardless of the room's cutoff — say so, because
      // the same action from the app may consume the credits. Not to a client that runs no credit
      // system, though: there the sentence describes machinery they have never seen.
      message: this.confirmBody(this.translate.instant(this.usesCredits ? 'BOOKINGS.CONFIRM.CANCEL' : 'BOOKINGS.CONFIRM.CANCEL_FREE'), [
        [this.translate.instant('BOOKINGS.FIELDS.ROOM'), this.roomLabel(booking)],
        [this.translate.instant('BOOKINGS.FIELDS.WHEN'), this.whenLabel(booking), 'ltr'],
        [this.translate.instant('BOOKINGS.FIELDS.BOOKED_BY'), booking.pass_name ?? ''],
        [this.translate.instant('BOOKINGS.CONFIRM.REFUND'), this.creditsLabel(booking.credits_charged - booking.credits_refunded), 'ltr'],
      ]),
      // A destructive confirm with a bare question, two same-weight buttons and no title reads as
      // a prompt, not a warning. Name the action, colour the destructive answer, and default the
      // focus to the safe one so a stray Enter does not cancel somebody's meeting.
      header: this.translate.instant('BOOKINGS.CONFIRM.CANCEL_HEADER'),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.translate.instant('BOOKINGS.ACTIONS.CANCEL'),
      rejectLabel: this.translate.instant('COMMON.BUTTONS.CANCEL'),
      acceptButtonStyleClass: 'p-button-danger',
      // `p-confirm-dialog-reject` is what makes `defaultFocus` work: PrimeNG resolves the focus
      // target by querying for that legacy class, which it no longer puts on the button itself —
      // so without it here the focus silently stays put and a stray Enter hits the danger button.
      rejectButtonStyleClass: 'p-button-text p-confirm-dialog-reject',
      defaultFocus: 'reject',
      accept: () => {
        this.bookingsService
          .cancel(booking.id)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe((result) => {
            if (result.success) {
              this.toast('BOOKINGS.MESSAGES.CANCELED')
              this.load()
            }
          })
      },
    })
  }

  evict(booking: Booking): void {
    this.confirmationService.confirm({
      // Same body as the cancel: it is the same kind of decision about the same kind of row, and
      // the one that ends a meeting somebody is sitting in is the LAST one to leave guessing.
      message: this.confirmBody(this.translate.instant('BOOKINGS.CONFIRM.EVICT'), [
        [this.translate.instant('BOOKINGS.FIELDS.ROOM'), this.roomLabel(booking)],
        [this.translate.instant('BOOKINGS.FIELDS.WHEN'), this.whenLabel(booking), 'ltr'],
        [this.translate.instant('BOOKINGS.FIELDS.BOOKED_BY'), booking.pass_name ?? ''],
      ]),
      header: this.translate.instant('BOOKINGS.CONFIRM.EVICT_HEADER'),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.translate.instant('BOOKINGS.ACTIONS.EVICT'),
      rejectLabel: this.translate.instant('COMMON.BUTTONS.CANCEL'),
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-text p-confirm-dialog-reject',
      defaultFocus: 'reject',
      accept: () => {
        this.bookingsService
          .end(booking.id)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe((result) => {
            if (result.success) {
              this.toast('BOOKINGS.MESSAGES.EVICTED')
              this.load()
            }
          })
      },
    })
  }

  /**
   * Quick +30 minutes. Anything else is an edit; PAPI re-checks conflicts and credits either way.
   *
   * Asked first, like the cancel beside it: it is one click on a small icon in a dense row, it
   * spends someone's credits, and — unlike the cancel — there is no undo button for it afterwards.
   * The confirmation is also the only place the admin is told what the extra half hour COSTS, which
   * is the part that varies: a rate-2.0 room charges twice a rate-1.0 one for the same 30 minutes.
   */
  extend(booking: Booking): void {
    const end = new Date(new Date(booking.end_time).getTime() + BookingsComponent.EXTEND_MINUTES * 60000)
    const area = this.areas.find((candidate) => candidate.area_id === booking.area_id)
    // Rounded to the cent: a 20-minute slot at rate 0.7 is 0.2333… credits, and a dialog that
    // quotes that many digits reads as a bug rather than as a price.
    const cost = area ? Math.round((BookingsComponent.EXTEND_MINUTES / 60) * Number(area.credit_rate_per_hour) * 100) / 100 : null

    this.confirmationService.confirm({
      message: this.confirmBody(this.translate.instant(this.usesCredits ? 'BOOKINGS.CONFIRM.EXTEND' : 'BOOKINGS.CONFIRM.EXTEND_FREE'), [
        [this.translate.instant('BOOKINGS.FIELDS.ROOM'), this.roomLabel(booking)],
        // The change itself, not just the new value — "10:00 → 10:30" is checkable at a glance
        // against the row the admin clicked; "10:30" alone is not.
        [
          this.translate.instant('BOOKINGS.CONFIRM.EXTEND_END'),
          `${this.clock(this.bookingTime.toWall(booking.end_time).getTime())} → ${this.clock(this.bookingTime.toWall(end).getTime())}`,
          'ltr',
        ],
        // Dropped rather than guessed at when the room is not in the loaded list.
        [this.translate.instant('BOOKINGS.CONFIRM.EXTEND_COST'), cost === null ? '' : this.creditsLabel(cost), 'ltr'],
      ]),
      header: this.translate.instant('BOOKINGS.CONFIRM.EXTEND_HEADER'),
      icon: 'pi pi-clock',
      acceptLabel: this.translate.instant('BOOKINGS.ACTIONS.EXTEND_30'),
      rejectLabel: this.translate.instant('COMMON.BUTTONS.CANCEL'),
      // Same legacy-class hook as the cancel below, on the button this dialog wants focused.
      acceptButtonStyleClass: 'p-confirm-dialog-accept',
      rejectButtonStyleClass: 'p-button-text',
      // Additive and reversible by editing, so unlike the cancel it may default to the action.
      defaultFocus: 'accept',
      accept: () => this.runExtend(booking, end),
    })
  }

  /**
   * Only `end_time` is sent — PAPI reads an explicit null as "clear this field", so passing the
   * whole booking back would erase the title the booker typed.
   *
   * Still routed through the allowance confirm: this dialog priced the extension, but only PAPI
   * knows what is left in the group's pool, and it can change between the two dialogs.
   */
  private runExtend(booking: Booking, end: Date): void {
    this.allowanceConfirm
      .runWithConfirmation((override) => this.bookingsService.update(booking.id, { end_time: end.toISOString(), ...(override ? { override_allowance: true } : {}) }))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (result.success) {
          this.toast('BOOKINGS.MESSAGES.EXTENDED')
          this.load()
        }
      })
  }

  /* ──────────────────────── confirmation dialog body ────────────────────── */

  /**
   * The shared body of the booking confirmations: the sentence, then the facts being agreed to.
   *
   * These dialogs decide somebody else's meeting, and a bare "Cancel this booking?" never says
   * WHICH one — the admin is answering it about a row in a grid of near-identical rows, having
   * clicked a small icon that sits next to two other small icons.
   *
   * Returned as HTML because PrimeNG renders `message` through Angular's sanitizer. Every value is
   * escaped on the way in, so a room named `<b>A</b>` shows as its own name and not as markup, and
   * a blank one drops its row rather than printing an empty label.
   *
   * Spans and not paragraphs: PrimeNG puts the message inside a `<span>`, and a `<p>` is not
   * allowed in one. The styling gives them their block layout.
   */
  private confirmBody(lead: string, facts: [string, string, string?][]): string {
    const rows = facts
      .filter(([, value]) => !!value)
      .map(([label, value, dir]) => `<div><dt>${this.escapeHtml(label)}</dt><dd dir="${dir ?? 'auto'}">${this.escapeHtml(value)}</dd></div>`)
      .join('')
    return `<span class="confirm-lead">${this.escapeHtml(lead)}</span><dl class="confirm-facts">${rows}</dl>`
  }

  private escapeHtml(value: string): string {
    return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
  }

  private roomLabel(booking: Booking): string {
    return booking.area_name || this.translate.instant('BOOKINGS.UNKNOWN_ROOM')
  }

  /** "12/08/2026 10:00–11:00" on the CLIENT'S clock — the same reading as the grid's When column. */
  private whenLabel(booking: Booking): string {
    const start = this.bookingTime.toWall(booking.start_time)
    const end = this.bookingTime.toWall(booking.end_time)
    const date = `${String(start.getDate()).padStart(2, '0')}/${String(start.getMonth() + 1).padStart(2, '0')}/${start.getFullYear()}`
    return `${date} ${this.clock(start.getTime())}–${this.clock(end.getTime())}`
  }

  /**
   * A credit figure with the unit this client actually reads it in — hours, or credits.
   *
   * Empty when there is no figure worth a line, which is two cases. A client that meters nothing
   * has rooms priced at the default rate they never looked at, so a cost row would quote a price
   * against a pool that does not exist — beside a sentence that has already been swapped for the
   * one which says only that the room is freed. And where credits ARE in play, "refund: 0" on a
   * booking that cost nothing says nothing either. `confirmBody` drops rows with an empty value.
   */
  private creditsLabel(amount: number): string {
    if (!this.usesCredits || !amount) return ''
    return `${amount} ${this.translate.instant(this.creditsUnitKey)}`
  }

  private toast(key: string): void {
    this.messageService.add({ severity: 'success', summary: this.translate.instant('SYSTEM.SUCCESS'), detail: this.translate.instant(key), life: 4000 })
  }

  /* ───────────────────────────── usage report ───────────────────────────── */

  loadUsage(): void {
    this.usageLoading = true
    this.cdr.markForCheck()
    const { from, to } = this.range
    this.bookingsService
      .getUsageReport(this.usageGroupBy, from, to, this.selectedEventId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        this.usageRows = result.success && result.data ? result.data.rows : []
        this.usageLoading = false
        this.cdr.markForCheck()
      })
  }

  /**
   * "100 hours a month" and "a big room costs more per hour" cannot both be hours, so the stored
   * unit is credits. When every room in this client is rate 1.0 the two are the same thing, and
   * saying "hours" is the honest label; otherwise it would be a lie.
   */
  get creditsUnitKey(): string {
    const everyRoomIsRateOne = this.areas.length > 0 && this.areas.every((area) => Number(area.credit_rate_per_hour) === 1)
    return everyRoomIsRateOne ? 'BOOKINGS.UNIT.HOURS' : 'BOOKINGS.UNIT.CREDITS'
  }

  /**
   * Whether credits are a thing in this client at all.
   *
   * Booking is sold two ways: metered against a group's pool, or simply "the room is yours if it is
   * free". A column of numbers, or a dialog promising a refund, explains machinery the second kind
   * has never seen — so it has to be off for them.
   *
   * BOTH halves have to hold, because either one alone is met by a client that meters nothing. The
   * room rate defaults to 1 and is left there by every admin who never thought about credits, so a
   * rate on its own says only "the default was not changed". The pool is the half that means a
   * person chose this: with no allowance anywhere, nothing is ever checked against anything, the
   * over-allowance dialog can never fire, and the charge on a booking is a number no one can spend
   * or exceed. And a group with an allowance that can only book rate-0 rooms never spends it.
   */
  get usesCredits(): boolean {
    return this.anyGroupHasAllowance && this.areas.some((area) => Number(area.credit_rate_per_hour) > 0)
  }

  /** Group + the two counts, plus the credit columns when there are any — for the empty row's span. */
  get usageColumnCount(): number {
    if (!this.usesCredits) return 3
    return this.usageGroupBy === 'client_role' ? 7 : 6
  }

  statusSeverity(status: BookingStatus): string {
    switch (status) {
      case 'in_use':
        return 'success'
      case 'booked':
        return 'info'
      case 'canceled':
        return 'danger'
      case 'no_show':
        return 'warn'
      default:
        return 'secondary'
    }
  }

  trackByBookingId(_index: number, booking: Booking): number {
    return booking.id
  }
}
